import { debounce } from '@nx-paeonia/utils-events';
import type { LitElement, ReactiveController, ReactiveControllerHost } from 'lit';
import { isNodeVisible } from '../is-visible/is-visible.js';

type SupportedFormElements = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

export class AttachInternalController implements ReactiveController {
  /** Private variables */
  private _slots: NodeListOf<HTMLSlotElement> | undefined = this.host.shadowRoot?.querySelectorAll<HTMLSlotElement>('slot');

  /** Protected variables */

  /** Public variables */
  public readonly _internals: ElementInternals;

  /** constructor & ngOn */
  constructor(private readonly host: ReactiveControllerHost & LitElement) {
    host.addController(this);

    this._internals = host.attachInternals();
  }

  hostUpdated(): void {
    this._slots = this.host.shadowRoot?.querySelectorAll<HTMLSlotElement>('slot');
    const slots = this._slots;

    if (!slots || slots.length === 0) {
      return;
    }

    slots.forEach((slot: HTMLSlotElement) => {
      slot.removeEventListener('slotchange', this._attachInternalElements.bind(this));
      slot.addEventListener('slotchange', this._attachInternalElements.bind(this));
    });

    this._attachInternalElements();
  }

  hostConnected(): void {
    const slots = this._slots;

    if (!slots || slots.length === 0) {
      return;
    }

    slots.forEach((slot: HTMLSlotElement) => {
      slot.removeEventListener('slotchange', this._attachInternalElements.bind(this));
      slot.addEventListener('slotchange', this._attachInternalElements.bind(this));
    });

    this._attachInternalElements();
  }

  /** Public methods */

  /** Protected methods */

  /** Private methods */
  private _attachInternalElements(): void {
    const inputs = this.host.querySelectorAll<SupportedFormElements>('input, select, textarea');
    const shadowInputs = this.host.shadowRoot?.querySelectorAll<SupportedFormElements>('input, select, textarea') ?? [];

    if (inputs.length === 0) {
      return;
    }

    const arrayOfInputs = Array.from(inputs);

    for (const shadowInput of shadowInputs) {
      // Push shadowInput into inputs if not already present
      if (!arrayOfInputs.includes(shadowInput)) {
        arrayOfInputs.push(shadowInput);
      }
    }
    const inputsEvt = debounce(() => {
      this.updateInternalElementValues(arrayOfInputs);
    }, 100);

    arrayOfInputs.forEach((input: SupportedFormElements) => {
      const invalidEvt = () => {
        this._internals.setValidity(input.validity, input.validationMessage, input);
      };
      input.removeEventListener('invalid', invalidEvt);
      input.addEventListener('invalid', invalidEvt);

      input.removeEventListener('input', inputsEvt);
      input.addEventListener('input', inputsEvt);
    });

    this.updateInternalElementValues(arrayOfInputs);
  }

  private updateInternalElementValues(inputs: Array<SupportedFormElements>): void {
    const formData = new FormData();

    inputs
      .filter((input) => !input.disabled)
      .filter((input) => (input instanceof HTMLInputElement && input.type === 'hidden') || isNodeVisible(input))
      .filter((input) => this.maybeRadioIsValid(input))
      .forEach((input: SupportedFormElements) => {
        if (input.name) {
          formData.append(input.name, input.value);
        }
      });

    this._internals.setFormValue(formData);
  }

  /**
   * Check if the input is a radio input and it is valid. If the input is a radio input, we need to check if it is checked and if it has a name attribute.
   * If the input is not a radio input, we can consider it valid.
   *
   * @param input
   * @private
   */
  private maybeRadioIsValid(input: SupportedFormElements): boolean {
    if (input instanceof HTMLInputElement && input.type === 'radio') {
      if (!input.name) {
        return false;
      }

      if (input.disabled) {
        return false;
      }

      return input.checked;
    }

    return true;
  }
}
